// Engine member state — users/{uid}/{engineMeta,engineExpressions,engineSituations}.
//
// Collection naming: the schema docs say members/{id}/expressions and
// members/{id}/sessions, but this app's tree is users/{uid}, `sessions` is
// already taken twice (top-level live-session calendar + per-user page
// analytics), and expressionState/{recId} is the RETIRING drill's state keyed
// on the same Airtable ids. The engine gets fresh, unambiguous names so drill
// history stays untouched for the later seeding pass (brief §7).
//
//   engineMeta/state           — one doc: streak, in-progress session, flags
//   engineExpressions/{recId}  — scheduling state per expression
//   engineSituations/{sitId}   — derived aggregates driving the map nodes
//
// All transition MATH lives in engineScheduler.js (pure); this file only
// moves documents. The one rule that matters here: an item outcome commits
// ATOMICALLY — expression state, situation aggregate, meta patch, and the
// item_outcome event land in one batch, so a crash mid-session can lose at
// most the item in flight, never leave the aggregates disagreeing with the
// states they summarize.
import { db } from "@/firebaseConfig";
import firebase from "firebase/compat/app";
import { noteFailure } from "@/utils/silentFailure";
import { markEngineDirty } from "@/services/engineBus";

const userRef = (uid) => db.collection("users").doc(String(uid));
const metaRef = (uid) => userRef(uid).collection("engineMeta").doc("state");

const EMPTY_STREAK = {
  current: 0, longest: 0, lastActiveDate: null,
  freezesRemaining: 0, freezeUsedDates: [], freezeGrantMonth: null,
};

// DEV-ONLY escape hatch. `VUE_APP_ENGINE_LOCAL=true npm run serve` keeps the
// app on LIVE data for everything real (classes, thumbnails, masterclasses,
// profile) while the member's own engine state lives in this browser instead
// of Firestore — the four engine subcollections are not readable under the
// live rules, and restoring them is a production change. The NODE_ENV test is
// compiled to `false` in a production build, so this and the import below are
// dead-code-eliminated; the shipped bundle only ever talks to Firestore.
const firestoreImpl = {
  /**
   * Load everything the scheduler needs, in parallel. Returns null on ANY
   * failure — a partial state map would make assembleQueue serve a first-run
   * queue to a long-time member, so callers must treat null as "do not start
   * a session", never as an empty state.
   */
  async fetchAll(uid) {
    if (!uid) return null;
    try {
      const [metaSnap, exprSnap, sitSnap] = await Promise.all([
        metaRef(uid).get(),
        userRef(uid).collection("engineExpressions").get(),
        userRef(uid).collection("engineSituations").get(),
      ]);
      const states = {};
      exprSnap.forEach((d) => { states[d.id] = d.data(); });
      const situations = {};
      sitSnap.forEach((d) => { situations[d.id] = d.data(); });
      return { meta: metaSnap.exists ? metaSnap.data() : null, states, situations };
    } catch (e) {
      noteFailure("engineStateService.fetchAll", e);
      return null;
    }
  },

  /**
   * Create the meta doc on first contact (no-op when it exists). The timezone
   * STRING is stored per G5 — scheduling is device-local, but any later
   * server-side job must read this, never assume KST.
   */
  async ensureMeta(uid, { timezone }) {
    try {
      const snap = await metaRef(uid).get();
      if (snap.exists) return true;
      await metaRef(uid).set({
        createdAt: firebase.firestore.FieldValue.serverTimestamp(),
        timezone: timezone || null,
        firstRunDone: false,
        streak: { ...EMPTY_STREAK },
        session: { inProgress: null },
        counters: { situationsComplete: 0, situationsMastered: 0 },
      });
      return true;
    } catch (e) {
      noteFailure("engineStateService.ensureMeta", e);
      return false;
    }
  },

  /**
   * Continuous auto-save of the session position (brief §6): item index +
   * completed-item outcomes. Item-boundary granularity only — a half-taught
   * expression never resumes mid-stage, so no stage state is saved.
   */
  async saveInProgress(uid, inProgress) {
    try {
      await metaRef(uid).set({ session: { inProgress } }, { merge: true });
      return true;
    } catch (e) {
      noteFailure("engineStateService.saveInProgress", e);
      return false;
    }
  },

  async clearInProgress(uid) {
    return this.saveInProgress(uid, null);
  },

  /**
   * Commit one item outcome atomically: expression state doc (wholesale —
   * the scheduler computed every field), situation aggregate (wholesale, from
   * situationAggregate — recomputed, self-healing), meta patch (merge:
   * in-progress cursor, streak, firstRunDone), and the item_outcome event.
   *
   * @param {string} uid
   * @param {object} c
   * @param {string} c.recId
   * @param {object} c.nextState       from applyNewOutcome/applyReviewOutcome
   * @param {string} c.situationId
   * @param {object} c.situationDoc    from situationAggregate().doc
   * @param {object} [c.metaPatch]     merged into engineMeta/state
   * @param {object} [c.event]         { type, payload, dateKey } appended with the batch
   * @returns {Promise<boolean>} committed?
   */
  async commitOutcome(uid, c) {
    if (!uid || !c || !c.recId || !c.nextState) return false;
    try {
      const batch = db.batch();
      batch.set(userRef(uid).collection("engineExpressions").doc(c.recId), {
        ...c.nextState,
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      });
      if (c.situationId && c.situationDoc) {
        batch.set(userRef(uid).collection("engineSituations").doc(c.situationId), {
          ...c.situationDoc,
          updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        });
      }
      if (c.metaPatch) {
        batch.set(metaRef(uid), c.metaPatch, { merge: true });
      }
      if (c.event && c.event.type) {
        batch.set(userRef(uid).collection("engineEvents").doc(), {
          v: 1,
          type: c.event.type,
          ...(c.event.payload || {}),
          dateKey: c.event.dateKey ?? null,
          loggedAt: firebase.firestore.FieldValue.serverTimestamp(),
        });
      }
      await batch.commit();
      markEngineDirty();
      return true;
    } catch (e) {
      noteFailure("engineStateService.commitOutcome", e);
      return false;
    }
  },

  /** Merge a streak (and any sibling meta fields) outside an item commit —
   *  session_end and 자유 복습 paths. */
  async patchMeta(uid, patch) {
    try {
      await metaRef(uid).set(patch, { merge: true });
      markEngineDirty();
      return true;
    } catch (e) {
      noteFailure("engineStateService.patchMeta", e);
      return false;
    }
  },

  /**
   * Playlist-side situation write (Home Spec §3.5): per-clip ended stamps,
   * playlist completion, and — when the situation is already 표현 완료 — the
   * 마스터 latch, plus its counter and event, in ONE batch. The mirror-image
   * outcome path (완료 arriving after the playlist) latches masteredAt inside
   * situationAggregate, so the two can land in either order.
   * @param {object} c  { situationId, playlist, masteredAt?, event? }
   */
  async patchSituationPlaylist(uid, c) {
    try {
      const batch = db.batch();
      const patch = { playlist: c.playlist };
      if (c.completedAt) patch.completedAt = c.completedAt; // doc self-heal
      if (c.masteredAt) {
        patch.masteredAt = c.masteredAt;
        batch.set(metaRef(uid), {
          counters: { situationsMastered: firebase.firestore.FieldValue.increment(1) },
        }, { merge: true });
      }
      batch.set(userRef(uid).collection("engineSituations").doc(c.situationId), patch, { merge: true });
      if (c.event && c.event.type) {
        batch.set(userRef(uid).collection("engineEvents").doc(), {
          v: 1,
          type: c.event.type,
          ...(c.event.payload || {}),
          dateKey: c.event.dateKey ?? null,
          loggedAt: firebase.firestore.FieldValue.serverTimestamp(),
        });
      }
      await batch.commit();
      markEngineDirty();
      return true;
    } catch (e) {
      noteFailure("engineStateService.patchSituationPlaylist", e);
      return false;
    }
  },

  /**
   * V2 situation-phase writes (docs: ENGINE_V2 Amendment 1). One merge patch
   * on the situation doc plus an optional event, atomically — the review
   * completion ({reviewCompletedAt, reviewLetters, reviewSitHints,
   * returnDueDate}) and later the return ({returnCompletedAt, ...}) both ride
   * this. Additive fields only: nothing here touches the v10 aggregate keys.
   */
  async patchSituation(uid, c) {
    if (!uid || !c || !c.situationId || !c.patch) return false;
    try {
      const batch = db.batch();
      batch.set(
        userRef(uid).collection("engineSituations").doc(c.situationId),
        { ...c.patch, updatedAt: firebase.firestore.FieldValue.serverTimestamp() },
        { merge: true },
      );
      if (c.event && c.event.type) {
        batch.set(userRef(uid).collection("engineEvents").doc(), {
          v: 1,
          type: c.event.type,
          ...(c.event.payload || {}),
          dateKey: c.event.dateKey ?? null,
          loggedAt: firebase.firestore.FieldValue.serverTimestamp(),
        });
      }
      await batch.commit();
      markEngineDirty();
      return true;
    } catch (e) {
      noteFailure("engineStateService.patchSituation", e);
      return false;
    }
  },

  /**
   * V2 expression-phase writes — patchSituation's twin on the expression doc.
   * The arc's phase stamps ({arcVideoAt}, {arcLearnAt}, {arcApplyAt}) and the
   * mid-clip resume position ride this. Additive fields only.
   */
  async patchExpression(uid, c) {
    if (!uid || !c || !c.recId || !c.patch) return false;
    try {
      const batch = db.batch();
      batch.set(
        userRef(uid).collection("engineExpressions").doc(c.recId),
        { ...c.patch, updatedAt: firebase.firestore.FieldValue.serverTimestamp() },
        { merge: true },
      );
      if (c.event && c.event.type) {
        batch.set(userRef(uid).collection("engineEvents").doc(), {
          v: 1,
          type: c.event.type,
          ...(c.event.payload || {}),
          dateKey: c.event.dateKey ?? null,
          loggedAt: firebase.firestore.FieldValue.serverTimestamp(),
        });
      }
      await batch.commit();
      markEngineDirty();
      return true;
    } catch (e) {
      noteFailure("engineStateService.patchExpression", e);
      return false;
    }
  },
};

// The require() sits INSIDE a branch whose condition webpack folds to `false`
// in a production build, so the branch — and the local store module with it —
// is dropped entirely. Verified: no engineLocal string survives in dist/.
let impl = firestoreImpl;
// Emulator mode outranks the local store: the escape hatch exists because the
// LIVE rules lack engine blocks, and the emulator loads the repo rules (which
// have them) — e2e must exercise the real service against emulator Firestore.
// NB: keyed on the FLAG alone, not NODE_ENV — a minified check build
// (VUE_APP_ENGINE_LOCAL=true npm run build) must keep local mode, or it
// boots against prod engine collections whose rules are deliberately not
// deployed and dies on 연결이 불안정해요 (seen live, phone, 2026-08-29).
// Real deploys never set the flag, so dead-code elimination there holds.
if (
  process.env.VUE_APP_ENGINE_LOCAL === "true"
  && process.env.VUE_APP_USE_EMULATOR !== "true"
) {
  // eslint-disable-next-line global-require
  impl = require("./engineStateLocal").default;
  // eslint-disable-next-line no-console
  console.warn("[engine] LOCAL engine state active (dev only) — classes and profile still come from live Firestore.");
}

export default impl;
